import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, money } from "@/lib/api";
import { partsDepuisResas } from "@/lib/partage";

const slug = (s) => s.replace(/\W+/g, "-").toLowerCase();
const num = (v) => (v === "" || v === null || v === undefined ? null : Number(String(v).replace(/\s/g, "").replace(",", ".")));
const fr = (v) => (v === null || v === undefined || v === "" ? "" : String(v).replace(".", ","));
const invalide = (v) => Number.isNaN(num(v)) || num(v) < 0;
const GRILLE = "grid grid-cols-3 gap-2 sm:grid-cols-[1fr_7rem_5.5rem_5.5rem]";

// Lignes de départ : parts actuelles (proposées d'après les séjours ou déjà saisies) + les deux logements de la page
const depart = (op, logements) => {
  const auto = op.parts?.length ? [] : partsDepuisResas(op.reservations, op.total, "logement").map((p) => ({ logement: p.nom, montant: p.montant }));
  const base = op.parts?.length ? op.parts : auto;
  const parts = base.map((p) => ({ logement: p.logement, montant: fr(p.montant), tps: fr(p.tps), tvq: fr(p.tvq) }));
  for (const l of logements) if (l && !parts.some((p) => p.logement === l)) parts.push({ logement: l, montant: base.length ? "0" : "", tps: "", tvq: "" });
  if (!base.length && parts[0]) parts[0].montant = fr(op.total);
  return parts;
};

const Ligne = ({ p, onChange }) => (
  <div className={`${GRILLE} items-center border-t border-slate-100 pt-2 sm:border-0 sm:pt-0`} data-testid={`repartir-ligne-${slug(p.logement)}`}>
    <span className="col-span-3 text-sm font-medium text-slate-800 sm:col-span-1 sm:truncate" title={p.logement}>{p.logement}</span>
    {[["montant", "Montant"], ["tps", "TPS"], ["tvq", "TVQ"]].map(([k, lib]) => (
      <label key={k} className="min-w-0">
        <span className="mb-0.5 block text-[10px] uppercase tracking-wide text-slate-500 sm:hidden">{lib}</span>
        <Input inputMode="decimal" value={p[k]} placeholder={k === "montant" ? "0,00" : "—"} onChange={(e) => onChange({ ...p, [k]: e.target.value })}
          className={`h-8 text-right tabular ${invalide(p[k]) ? "border-red-400 focus-visible:ring-red-400" : ""}`} data-testid={`repartir-${slug(p.logement)}-${k}`} />
      </label>
    ))}
  </div>
);

export const RepartirLogementsDialog = ({ op, logements, onClose, onSaved }) => {
  const [lignes, setLignes] = useState([]);
  const [envoi, setEnvoi] = useState(false);
  useEffect(() => { if (op) setLignes(depart(op, logements)); }, [op, logements]);
  if (!op) return null;
  const somme = lignes.reduce((s, p) => s + (num(p.montant) || 0), 0);
  const ecart = Math.round((op.total - somme) * 100) / 100;
  const erreur = lignes.some((p) => ["montant", "tps", "tvq"].some((k) => invalide(p[k])));
  const enregistrer = async () => {
    setEnvoi(true);
    try {
      const parts = lignes.map((p) => ({ logement: p.logement, montant: num(p.montant) || 0, tps: num(p.tps), tvq: num(p.tvq) }));
      await api.put(`/reaffecter/operations/${op.id}/parts`, { parts });
      toast.success("Répartition enregistrée");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setEnvoi(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] w-[calc(100vw-1.5rem)] max-w-xl overflow-y-auto" data-testid="repartir-dialog">
        <DialogHeader>
          <DialogTitle>Répartir le versement entre les logements</DialogTitle>
          <DialogDescription>{op.date} · {op.description} · {money(op.total)}. Montants proposés d'après les séjours payés ; modifiez-les et saisissez les taxes si besoin. Les réservations ne bougent pas.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <div className={`${GRILLE} hidden text-[11px] uppercase tracking-wide text-slate-500 sm:grid`}>
            <span>Logement</span><span className="text-right">Montant</span><span className="text-right">TPS</span><span className="text-right">TVQ</span>
          </div>
          {lignes.map((p, i) => <Ligne key={p.logement} p={p} onChange={(n) => setLignes(lignes.map((x, j) => (j === i ? n : x)))} />)}
          <p className={`pt-1 text-right text-xs ${ecart || erreur ? "text-red-600" : "text-emerald-700"}`} data-testid="repartir-ecart">
            {erreur ? "Un montant n'est pas un nombre valide (ex. 1234,56)" : `Réparti : ${money(somme)} sur ${money(op.total)}${ecart > 0 ? ` · reste ${money(ecart)}` : ecart < 0 ? ` · dépasse de ${money(-ecart)}` : " · complet"}`}
          </p>
        </div>
        <DialogFooter className="flex-row flex-wrap justify-end gap-2">
          <Button variant="outline" onClick={onClose} data-testid="repartir-annuler">Annuler</Button>
          <Button className="bg-emerald-700 hover:bg-emerald-800" disabled={envoi || !!ecart || erreur} onClick={enregistrer} data-testid="repartir-enregistrer">
            {envoi && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
